import {
  HeartPulse, ShoppingCart, Building2, Landmark, GraduationCap,
  Truck, UtensilsCrossed, ShieldCheck, Car, Briefcase
} from "lucide-react";

export const industries = [
  {
    key: "healthcare",
    icon: HeartPulse,
    en: "Healthcare",
    ar: "الرعاية الصحية",
    short_en: "HIPAA-minded patient portals, tele-health and clinical workflow automation.",
    short_ar: "بوابات مرضى وخدمات صحية عن بعد وأتمتة سير العمل السريري.",
    valueProp_en: "Patient trust is earned through speed and clarity — every extra phone call or paper form is a chance to lose someone to a competitor or a missed appointment. We build the digital layer that keeps care coordinated across clinics, providers and insurers, in both languages, without adding to clinical staff's workload.",
    valueProp_ar: "ثقة المريض تُكسب بالسرعة والوضوح — كل مكالمة أو نموذج ورقي إضافي فرصة لفقدانه لصالح منافس أو موعد فائت. نبني الطبقة الرقمية التي تُبقي الرعاية منسقة عبر العيادات والأطباء وشركات التأمين، باللغتين، دون إضافة عبء على الطاقم الطبي.",
    useCases_en: ["Bilingual patient portals & AI triage", "Tele-health & remote consultation platforms", "Clinical workflow automation & FHIR integration"],
    useCases_ar: ["بوابات مرضى ثنائية اللغة وفرز بالذكاء الاصطناعي", "منصات رعاية صحية عن بعد واستشارات", "أتمتة سير العمل السريري وتكامل FHIR"],
    stat: { k: "+47%", label_en: "Online booking rate lift", label_ar: "ارتفاع معدل الحجز الإلكتروني" },
  },
  {
    key: "retail",
    icon: ShoppingCart,
    en: "Retail",
    ar: "التجزئة",
    short_en: "Omnichannel commerce, POS integrations, loyalty and AI personalization.",
    short_ar: "تجارة متعددة القنوات وتكامل نقاط البيع والولاء والتخصيص.",
    valueProp_en: "Gulf shoppers move fluidly between Instagram, WhatsApp, marketplace apps and in-store — and expect the same personalization and stock accuracy everywhere. We build the commerce and data infrastructure that makes omnichannel actually omnichannel, not three disconnected systems.",
    valueProp_ar: "متسوقو الخليج ينتقلون بسلاسة بين إنستغرام وواتساب وتطبيقات الأسواق والمتجر الفعلي — ويتوقعون التخصيص ودقة المخزون ذاتها في كل مكان. نبني بنية التجارة والبيانات التي تجعل تعدد القنوات متكاملاً فعلاً، لا ثلاثة أنظمة منفصلة.",
    useCases_en: ["AI-personalized product recommendations", "Unified inventory across online & physical stores", "Loyalty & retention automation"],
    useCases_ar: ["توصيات منتجات مخصصة بالذكاء الاصطناعي", "مخزون موحّد بين المتجر الإلكتروني والفعلي", "أتمتة الولاء والاحتفاظ بالعملاء"],
    stat: { k: "+42%", label_en: "Conversion rate lift", label_ar: "ارتفاع معدل التحويل" },
  },
  {
    key: "real-estate",
    icon: Building2,
    en: "Real Estate",
    ar: "العقارات",
    short_en: "Listing platforms, CRM, virtual tours and lead routing.",
    short_ar: "منصات العقارات و CRM وجولات افتراضية.",
    valueProp_en: "Listings alone don't close deals — speed of response and trust in the process do. We build platforms that route qualified leads instantly and give buyers a transparent, self-serve view of the process.",
    valueProp_ar: "الإعلانات وحدها لا تُغلق الصفقات — سرعة الاستجابة والثقة بالعملية هي التي تفعل. نبني منصات توجّه العملاء المؤهلين فوراً وتمنح المشترين رؤية شفافة وذاتية الخدمة للعملية.",
    useCases_en: ["Listing platforms with virtual tours", "CRM & automated lead routing", "Digital document & contract workflows"],
    useCases_ar: ["منصات إعلانات مع جولات افتراضية", "إدارة علاقات عملاء وتوجيه تلقائي", "سير عمل مستندات وعقود رقمي"],
    stat: { k: "3.1×", label_en: "Faster lead response", label_ar: "استجابة أسرع للعملاء" },
  },
  {
    key: "finance",
    icon: Landmark,
    en: "Finance",
    ar: "التمويل",
    short_en: "KYC, AML, digital onboarding, dashboards and fintech launches.",
    short_ar: "KYC و AML وتدشين رقمي ولوحات ومنتجات مالية.",
    valueProp_en: "Regulatory scrutiny in Gulf fintech is real and rising — KYC, AML and data residency aren't optional line items. We build compliant-by-design digital onboarding and growth infrastructure that legal teams approve and customers actually finish.",
    valueProp_ar: "التدقيق التنظيمي في التقنية المالية الخليجية حقيقي ومتصاعد — اعرف عميلك ومكافحة غسل الأموال وإقامة البيانات ليست بنوداً اختيارية. نبني تدشيناً رقمياً متوافقاً بالتصميم وبنية نمو توافق عليها الفرق القانونية ويُكملها العملاء فعلاً.",
    useCases_en: ["KYC/AML-compliant digital onboarding", "Fraud detection & risk scoring models", "Growth attribution & CAC optimization"],
    useCases_ar: ["تدشين رقمي متوافق مع KYC و AML", "نماذج كشف احتيال وتقييم مخاطر", "إسناد نمو وتحسين تكلفة الاكتساب"],
    stat: { k: "−31%", label_en: "CAC reduction", label_ar: "انخفاض تكلفة الاكتساب" },
  },
  {
    key: "education",
    icon: GraduationCap,
    en: "Education",
    ar: "التعليم",
    short_en: "LMS, bilingual student portals, AI tutoring and content delivery.",
    short_ar: "منصات تعلم وبوابات ثنائية اللغة ومساعد تعليم ذكي.",
    valueProp_en: "Bilingual learners need more than a translated LMS — they need content, assessment and tutoring designed for Arabic and English from the start. We build learning platforms that actually get used, not just procured.",
    valueProp_ar: "المتعلمون ثنائيو اللغة يحتاجون أكثر من نظام إدارة تعلم مترجم — يحتاجون محتوى وتقييماً وتدريساً مصممة للعربية والإنجليزية منذ البداية. نبني منصات تعلم تُستخدم فعلاً، لا تُشترى فقط.",
    useCases_en: ["Bilingual student portals & LMS", "AI-assisted tutoring & content delivery", "Enrollment & fee management automation"],
    useCases_ar: ["بوابات طلاب ثنائية اللغة وأنظمة إدارة تعلم", "تدريس مدعوم بالذكاء الاصطناعي وتقديم محتوى", "أتمتة التسجيل وإدارة الرسوم"],
    stat: { k: "+58%", label_en: "Student engagement lift", label_ar: "ارتفاع تفاعل الطلاب" },
  },
  {
    key: "logistics",
    icon: Truck,
    en: "Logistics",
    ar: "الخدمات اللوجستية",
    short_en: "TMS, last-mile apps, OCR and route optimization.",
    short_ar: "TMS وتطبيقات الميل الأخير و OCR وتحسين المسارات.",
    valueProp_en: "Every manual document handoff in a cross-border shipment is a chance to miss an SLA. We automate the paperwork-heavy middle of logistics — customs, manifests, proof of delivery — so your ops team manages exceptions, not data entry.",
    valueProp_ar: "كل تسليم مستندات يدوي في شحنة عابرة للحدود فرصة لتفويت اتفاقية خدمة. نؤتمت الجزء الورقي الثقيل من اللوجستيات — الجمارك والبيانات وإثبات التسليم — ليدير فريق عملياتكم الاستثناءات لا إدخال البيانات.",
    useCases_en: ["Computer-vision document processing", "Route optimization & last-mile apps", "ERP-to-ERP workflow automation"],
    useCases_ar: ["معالجة مستندات بالرؤية الحاسوبية", "تحسين المسارات وتطبيقات الميل الأخير", "أتمتة سير العمل بين أنظمة تخطيط الموارد"],
    stat: { k: "99.4%", label_en: "SLA accuracy achieved", label_ar: "دقة اتفاقية خدمة محققة" },
  },
  {
    key: "hospitality",
    icon: UtensilsCrossed,
    en: "Hospitality",
    ar: "الضيافة",
    short_en: "Bookings, guest apps, revenue management and PMS integrations.",
    short_ar: "حجوزات وتطبيقات ضيوف وإدارة إيرادات.",
    valueProp_en: "Third-party booking platforms take the margin and the guest relationship. We build direct booking engines and guest experience apps that bring both back — plus the loyalty data to keep guests coming back without a commission.",
    valueProp_ar: "منصات الحجز الخارجية تأخذ الهامش وعلاقة النزيل. نبني محركات حجز مباشرة وتطبيقات تجربة نزلاء تُعيد الاثنين — إضافة لبيانات الولاء التي تُبقي النزلاء عائدين دون عمولة.",
    useCases_en: ["Direct booking engines with dynamic pricing", "Guest apps for in-stay requests & upsells", "PMS integration & revenue management"],
    useCases_ar: ["محركات حجز مباشرة بتسعير ديناميكي", "تطبيقات نزلاء لطلبات الإقامة والبيع الإضافي", "تكامل أنظمة إدارة الفنادق والإيرادات"],
    stat: { k: "+31%", label_en: "Direct booking growth", label_ar: "نمو الحجوزات المباشرة" },
  },
  {
    key: "government",
    icon: ShieldCheck,
    en: "Government",
    ar: "الحكومة",
    short_en: "Citizen portals, digital identity, AI assistance and automation.",
    short_ar: "بوابات مواطنين وهوية رقمية ومساعد ذكاء اصطناعي.",
    valueProp_en: "Citizens judge government digital services against the best consumer apps they use, not against the old paper process. We build citizen portals and internal automation that meet that bar — secure, fast and accessible by design, with the audit trail regulators require.",
    valueProp_ar: "المواطنون يقيّمون الخدمات الحكومية الرقمية مقارنة بأفضل تطبيقات الاستهلاك التي يستخدمونها، لا بالعملية الورقية القديمة. نبني بوابات مواطنين وأتمتة داخلية ترتقي لهذا المستوى — آمنة وسريعة ومتاحة بالتصميم، مع سجل تدقيق يلبي متطلبات الجهات التنظيمية.",
    useCases_en: ["Citizen self-service portals", "Digital identity & secure authentication", "AI-assisted case processing & document automation"],
    useCases_ar: ["بوابات خدمة ذاتية للمواطنين", "هوية رقمية ومصادقة آمنة", "معالجة معاملات مدعومة بالذكاء الاصطناعي وأتمتة مستندات"],
    stat: { k: "−68%", label_en: "Service processing time", label_ar: "وقت معالجة الخدمة" },
  },
  {
    key: "automotive",
    icon: Car,
    en: "Automotive",
    ar: "السيارات",
    short_en: "Dealer portals, inventory, test-drive booking and service tooling.",
    short_ar: "بوابات موزعين ومخزون وخدمات ما بعد البيع.",
    valueProp_en: "Dealers and service centers lose bookings to whichever platform responds fastest. We build inventory, booking and service tools that keep the whole customer lifecycle in one connected system.",
    valueProp_ar: "الوكلاء ومراكز الخدمة يخسرون الحجوزات لصالح أي منصة تستجيب أسرع. نبني أدوات مخزون وحجز وخدمة تُبقي دورة حياة العميل كاملة في نظام واحد متصل.",
    useCases_en: ["Dealer inventory & listing portals", "Test-drive & service booking automation", "After-sales CRM & follow-up workflows"],
    useCases_ar: ["بوابات مخزون وإعلانات للوكلاء", "أتمتة حجز القيادة التجريبية والخدمة", "إدارة علاقات عملاء ما بعد البيع ومتابعة"],
    stat: { k: "2.4×", label_en: "Faster service booking", label_ar: "حجز خدمة أسرع" },
  },
  {
    key: "professional",
    icon: Briefcase,
    en: "Professional Services",
    ar: "الخدمات المهنية",
    short_en: "Practice management, client portals, proposal and billing tools.",
    short_ar: "إدارة الممارسة وبوابات العملاء والفوترة.",
    valueProp_en: "Billable hours lost to admin — proposals, invoicing, client status updates — are hours not spent on client work. We build practice management and client portal tools that give that time back.",
    valueProp_ar: "ساعات العمل المفوترة الضائعة في الأعمال الإدارية — العروض والفوترة وتحديثات حالة العميل — ساعات لا تُصرف على عمل العميل. نبني أدوات إدارة ممارسة وبوابات عملاء تُعيد ذلك الوقت.",
    useCases_en: ["Client portals & project status dashboards", "Proposal & billing automation", "Document & knowledge management"],
    useCases_ar: ["بوابات عملاء ولوحات متابعة المشاريع", "أتمتة العروض والفوترة", "إدارة المستندات والمعرفة"],
    stat: { k: "6+ hrs", label_en: "Admin time saved weekly", label_ar: "ساعات إدارية موفرة أسبوعياً" },
  },
];

export const getIndustry = (key) => industries.find((i) => i.key === key);
